/**
 * AEAT fiscal BFF client (server-side integration only).
 */

import { get, post, upload } from '../../shared/apiClient';
import { buildUrl, ENDPOINTS } from '../../shared/endpoints';
import { HttpError } from '../../shared/httpTypes';
import {
  AEAT_TRANSPORT_HINT,
  FISCAL_NOT_AUTHORIZED_HINT,
  RATIFICATION_REQUIRED_FILE_HINT,
  type AeatBffFlow,
} from './aeatMethodMapping';

export interface AeatParsedError {
  code?: string;
  message: string;
  aeat_codigo?: string;
  ratification_urls?: Record<string, string>;
}

export interface RatificationResponse {
  domicilio_ratificado: boolean;
  mock?: boolean;
  ratification_urls?: Record<string, string>;
}

export interface FiscalPrefillAnswers {
  [questionId: string]: any;
}

export interface FiscalProvenance {
  [questionId: string]: {
    source: 'aeat' | 'derived' | 'user' | 'user_overridden' | 'aeat_conflicted';
    aeat_segment: string;
    confidence: 'high' | 'medium' | 'low';
    data_reliability?: 'high' | 'medium' | 'low';
    original_raw: string;
  };
}

export interface FiscalDiscrepancy {
  field: string;
  aeat_value: any;
  user_value: any;
  aeat_segment: string;
  severity: 'error' | 'warning';
}

export interface FiscalParsedData {
  prefill_answers: FiscalPrefillAnswers;
  provenance: FiscalProvenance;
  metadata: Record<string, any>;
  field_count: number;
  discrepancies?: FiscalDiscrepancy[];
  taxpayer: {
    tax_id: string;
    full_name_raw: string;
    first_name: string;
    first_surname: string;
    second_surname: string;
    name_confidence: 'high' | 'medium' | 'low';
    date_of_birth: string;
    iban: string;
  } | null;
  address: {
    street: string;
    apt_unit: string;
    postal_code: string;
    city: string;
    province: string;
    country: string;
  } | null;
  income_records: Array<{
    payer_nif: string;
    payer_name: string;
    income_key: string;
    gross_amount: number;
    withholdings: number;
  }>;
  ejercicio: string;
  parse_warnings: string[];
}

export interface FiscalDataResponse {
  content_type: string;
  size: number;
  body_base64: string;
  mock?: boolean;
  parsed?: FiscalParsedData | null;
}

export function parseFastApiError(err: unknown): AeatParsedError {
  if (err instanceof HttpError) {
    const d = err.data as { detail?: unknown } | undefined;
    const detail = d?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      const o = detail as {
        code?: string;
        message?: string;
        aeat_codigo?: string;
        ratification_urls?: Record<string, string>;
      };
      return {
        code: o.code,
        message: o.message || err.message,
        aeat_codigo: o.aeat_codigo,
        ratification_urls: o.ratification_urls,
      };
    }
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: 'Unknown error' };
}

/** Adds operator-facing hints for stable BFF codes (e.g. FISCAL_NOT_AUTHORIZED / AEAT 1310). */
export function formatAeatUserFacingError(parsed: AeatParsedError): string {
  if (parsed.code === 'FISCAL_NOT_AUTHORIZED') {
    return `${parsed.message}\n\n${FISCAL_NOT_AUTHORIZED_HINT}`;
  }
  if (parsed.code === 'RATIFICATION_REQUIRED_FILE') {
    return `${parsed.message}\n\n${RATIFICATION_REQUIRED_FILE_HINT}`;
  }
  if (parsed.code === 'AEAT_TRANSPORT') {
    return `${parsed.message}\n\n${AEAT_TRANSPORT_HINT}`;
  }
  return parsed.message;
}

export async function aeatCreateSession(flow: AeatBffFlow): Promise<string> {
  const { data } = await post<{ flow: AeatBffFlow }, { session_id: string; flow: string }>(
    ENDPOINTS.aeatFiscal.createSession,
    { flow }
  );
  return data.session_id;
}

export async function aeatUploadCertificate(
  sessionId: string,
  file: File,
  password: string
): Promise<{ ok: boolean; registered: boolean }> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.certificateUpload, { params: { sessionId } });
  const fd = new FormData();
  fd.append('file', file);
  fd.append('password', password);
  const { data } = await upload<{ ok: boolean; registered: boolean }>(url, fd);
  return data;
}

export async function aeatRatification(
  sessionId: string,
  body: { nif: string; ejercicio: string; p12_password?: string }
): Promise<RatificationResponse> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.ratification, { params: { sessionId } });
  const { data } = await post<typeof body, RatificationResponse>(url, body);
  return data;
}

export async function aeatClaveStart(
  sessionId: string,
  body: { nif: string; fecha?: string | null; soporte?: string | null }
): Promise<{ status: string; movil?: string; mock?: boolean }> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.claveStart, { params: { sessionId } });
  const { data } = await post(url, body);
  return data as { status: string; movil?: string; mock?: boolean };
}

export async function aeatClavePin(sessionId: string, pin: string): Promise<{ status: string; mock?: boolean }> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.clavePin, { params: { sessionId } });
  const { data } = await post(url, { pin });
  return data as { status: string; mock?: boolean };
}

export async function aeatClaveResend(
  sessionId: string
): Promise<{ status: string; movil?: string; sms_requested_at?: string; resend_count?: number; mock?: boolean }> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.claveResend, { params: { sessionId } });
  const { data } = await post(url, {});
  return data as { status: string; movil?: string; sms_requested_at?: string; resend_count?: number; mock?: boolean };
}

export async function aeatGetSession(
  sessionId: string
): Promise<{ session_id: string; flow: string; step: string; nif_masked: string; ratification_checked: boolean; domicilio_ratificado: boolean | null; clave_mobile_hint?: string; user_cert_registered: boolean }> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.getSession, { params: { sessionId } });
  const { data } = await get(url);
  return data as any;
}

export async function aeatReference(
  sessionId: string,
  body: { nif: string; reference: string }
): Promise<{ status: string; mock?: boolean }> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.reference, { params: { sessionId } });
  const { data } = await post(url, body);
  return data as { status: string; mock?: boolean };
}

export async function aeatFiscalData(
  sessionId: string,
  body: {
    nif: string;
    pdp: string;
    p12_password?: string;
    flow_answers?: Record<string, unknown>;
    flow_session_id?: string;
  }
): Promise<FiscalDataResponse> {
  const url = buildUrl(ENDPOINTS.aeatFiscal.fiscalData, { params: { sessionId } });
  const { data } = await post<typeof body, FiscalDataResponse>(url, body);
  return data;
}
